import React from 'react';
import { Building2, CheckCircle, TrendingUp, Users, Package, BarChart3, Target } from 'lucide-react';

export default function BigBoxRetail() {
  const retailers = [
    { name: 'TJ Maxx', logo: 'https://logo.clearbit.com/tjmaxx.tjx.com' },
    { name: 'Marshalls', logo: 'https://logo.clearbit.com/marshalls.com' },
    { name: 'HomeSense', logo: 'https://logo.clearbit.com/homesense.ca' },
    { name: 'Winners', logo: 'https://logo.clearbit.com/winners.ca' },
    { name: 'Walmart Canada', logo: 'https://logo.clearbit.com/walmart.ca' }
  ];

  const framework = [
    {
      phase: 'Phase 1: Retail Readiness Assessment',
      duration: '2-3 weeks',
      activities: [
        'Product portfolio analysis for big box compatibility',
        'Pricing architecture review and margin optimization',
        'Packaging compliance audit (barcode, labeling, case pack)',
        'Supply chain capacity evaluation',
        'Competitive landscape mapping'
      ]
    },
    {
      phase: 'Phase 2: Strategic Positioning',
      duration: '3-4 weeks',
      activities: [
        'Buyer persona development for each target retailer',
        'Value proposition crafting (why your brand, why now)',
        'Assortment planning by channel and season',
        'Promotional calendar and markdown strategy',
        'Test market identification'
      ]
    },
    {
      phase: 'Phase 3: Relationship Building',
      duration: '4-8 weeks',
      activities: [
        'Warm introductions to category buyers',
        'Line review presentations with sell sheets',
        'Sample coordination and follow-up',
        'Terms negotiation (payment, returns, markdown money)',
        'Vendor setup and compliance documentation'
      ]
    },
    {
      phase: 'Phase 4: Launch Execution',
      duration: '8-12 weeks',
      activities: [
        'Purchase order management and fulfillment',
        'In-store merchandising support and planogram compliance',
        'Co-marketing program execution',
        'Sales data monitoring and reorder management',
        'Performance reporting and optimization'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The 3-6-12 Framework',
      icon: Target,
      description: 'Our proprietary approach: 3 months to first PO, 6 months to prove performance, 12 months to scale nationwide. We compress the typical 18-24 month big box entry timeline by 40%.'
    },
    {
      title: 'Margin Preservation System',
      icon: TrendingUp,
      description: 'Unlike traditional brokers who race to the bottom on price, we protect your margins through strategic positioning, value-added programs, and intelligent assortment planning.'
    },
    {
      title: 'Buyer Relationship Network',
      icon: Users,
      description: 'Direct relationships with 150+ category buyers across major retailers. We provide warm introductions, not cold pitches—dramatically increasing your approval rate.'
    },
    {
      title: 'Supply Chain Integration',
      icon: Package,
      description: 'End-to-end fulfillment support from factory to store shelf. We handle EDI setup, routing guide compliance, chargebacks management, and inventory optimization.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Building2 size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Big Box Retail Sales
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Scale through major retail partnerships
              </p>
            </div>
          </div>
        </div>

        {/* Our Retail Partners */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Our Retail Partnership Network</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <p className="text-lg text-gray-700 mb-8">
              We maintain strong relationships with leading big box retailers across North America, providing our brand partners with direct access to category buyers and streamlined onboarding.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {retailers.map((retailer, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 flex items-center justify-center hover:border-[#38A3A5] transition-all">
                  <div className="text-center">
                    <div className="mb-3 flex items-center justify-center">
                      <img 
                        src={retailer.logo} 
                        alt={retailer.name}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{display: 'none'}} className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Building2 size={24} className="text-gray-400" />
                      </div>
                    </div>
                    <p className="font-semibold text-sm text-gray-800">{retailer.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The 3-6-12 Big Box Entry Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Most brands take 18-24 months to successfully enter big box retail—if they succeed at all. Our systematic approach compresses this timeline while increasing success rates. Here's how we do it differently:
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Duration: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Traditional brokers take 15-20% commissions and leave you managing complexity. We partner differently—combining strategic advisory, hands-on execution, and deep retail relationships to drive sustainable growth.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Proven Results</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">73%</div>
              <div className="text-lg opacity-90">Buyer approval rate for brands we represent</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">12 mo</div>
              <div className="text-lg opacity-90">Average timeline from first meeting to national distribution</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">$8.2M</div>
              <div className="text-lg opacity-90">Average annual revenue added per brand partner</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Scale Through Big Box Retail?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss how we can help you access major retail partnerships while preserving your margins.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
